import type { GeneratedSchema, PermitNodes, PermitSchema, SchemaTree } from '../types';
import { PermitNode, SchemaNode } from '../node';
import { keySafe } from '../utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Permit<T extends SchemaTree<any>> {
	/** Access Schema */
	readonly on: PermitSchema<T>;

	private _nodes = new Map<string, PermitNode>();
	private schema: T;
	private scopes: string[];

	constructor(schema: T, scopes: string[] = []) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const travel = <V extends GeneratedSchema>(branch: V) => {
			type Node = PermitBranch<V> | PermitNode | Record<string, unknown>;
			const entries: [path: string, node: Node][] = [];
			for (const key in branch) {
				if (Object.prototype.hasOwnProperty.call(branch, key)) {
					const value = branch[key];
					if (typeof key !== 'string') break;
					if (value instanceof SchemaNode) {
						// value is schema node instance
						// create permit node directly to value
						const node = new PermitNode(value, scopes.includes(value.$scope));
						this._nodes.set(node.$scope, node);
						entries.push([key, node]);
					} else if (Object.hasOwn(value, '$scope') && Object.hasOwn(value, '$description')) {
						// value is native object that has $scope and $description
						// create schema node first then do the rest
						const schemanode = new SchemaNode(
							(value as SchemaNode).$scope,
							(value as SchemaNode).$description
						);
						const node = new PermitNode(schemanode, scopes.includes(schemanode.$scope));
						this._nodes.set(node.$scope, node);
						entries.push([key, node]);
					} else if (key.startsWith('$') || key.startsWith('_')) {
						// passthrough utility
						entries.push([key, value]);
					} else {
						// value is on branch not node
						const node = travel(value);
						const $branch = new PermitBranch(node);
						entries.push([key, Object.assign($branch, node)]);
					}
				}
			}

			return Object.fromEntries(entries) as PermitNodes<V>;
		};

		this.scopes = scopes;
		this.schema = schema;
		const root = travel(this.schema);
		const tree = new PermitBranch(root);
		this.on = Object.assign(tree, root) as PermitSchema<T>;
	}

	static getNodeScope(scope: string | SchemaNode | PermitNode) {
		return typeof scope === 'string' ? scope : scope.$scope;
	}

	/* -------------------------------------------------------------------------- */

	/** get nodes that has active access or granted */
	get granted() {
		return this.on.$granted;
	}

	/** get nodes that is inactive or denied access */
	get denied() {
		return this.on.$denied;
	}

	/** get list of permit node */
	get nodes() {
		return this.on.$nodes;
	}

	/** reset scope to default state */
	reset() {
		this.on.$each((node) => {
			if (this.scopes.includes(node.$scope)) {
				node.$able = true;
			} else {
				node.$able = false;
			}
		});
	}

	/** grant access to scope */
	grant(...scopes: (string | SchemaNode)[]) {
		for (let i = 0; i < scopes.length; i++) {
			const scope = Permit.getNodeScope(scopes[i]);
			const node = this._nodes.get(scope);
			if (node) node.$able = true;
		}
	}

	/** deny access to scope */
	deny(...scopes: (string | SchemaNode | PermitNode)[]) {
		for (let i = 0; i < scopes.length; i++) {
			const scope = Permit.getNodeScope(scopes[i]);
			const node = this._nodes.get(scope);
			if (node) node.$able = false;
		}
	}

	ableTo(scope: string | SchemaNode | PermitNode) {
		const $scope = Permit.getNodeScope(scope);
		const node = this._nodes.get($scope);
		if (node) return node.$able;
		return false;
	}

	get(scope: string | SchemaNode) {
		const $scope = Permit.getNodeScope(scope);
		return this._nodes.get($scope);
	}

	has(scope: string | SchemaNode | PermitNode) {
		const $scope = Permit.getNodeScope(scope);
		const node = this._nodes.get($scope);
		return node !== undefined;
	}

	child(scope: string) {
		const child: PermitNode[] = [];
		this.on.$each((node) => node.$scope.startsWith(scope) && child.push(node));
		return child;
	}

	/** loop through each node */
	$each(fn: (node: PermitNode) => void) {
		this.on.$each(fn);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class PermitBranch<T extends Record<string, any>> {
	private $tree: PermitNodes<T>;

	constructor(nodes: PermitNodes<T>) {
		this.$tree = nodes;
	}

	$each(fn: (node: PermitNode) => void) {
		for (const key in this.$tree) {
			if (keySafe(key) && Object.prototype.hasOwnProperty.call(this.$tree, key)) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const node: any = this.$tree[key];
				if (node instanceof PermitNode) {
					fn(node);
				} else if (node instanceof PermitBranch) {
					node.$each(fn);
				}
			}
		}
	}

	get $able() {
		let valid = true;
		this.$each((node) => (valid = valid && node.$able));
		return valid;
	}

	get $doable() {
		let valid = false;
		this.$each((node) => (valid = valid || node.$able));
		return valid;
	}

	get $disable() {
		return this.$doable === false;
	}

	get $nodes() {
		const nodes: PermitNode[] = [];
		this.$each((node) => nodes.push(node));
		return nodes;
	}

	get $granted() {
		const nodes: PermitNode[] = [];
		this.$each((node) => {
			if (node.$able) nodes.push(node);
		});

		return nodes;
	}

	get $denied() {
		const nodes: PermitNode[] = [];
		this.$each((node) => {
			if (!node.$able) nodes.push(node);
		});

		return nodes;
	}
}